import { defineStore } from 'pinia'
import {
  getAppCode, getCustomJWT,
  getRefreshToken,
  getToken,
  getTokenDify,
  getTokenPassPort, removeAppCode, removeCustomJWT,
  removeRefreshToken, removeToken,
  removeTokenDify, setAppCode, setCustomJWT, setRefreshToken,
  setToken, setTokenDify,
  setTokenPassPort,
} from './helper'
import { router } from '@/router'
import { store } from '@/store/helper'
import { loginApi, loginByGoogle, logoutApi } from '@/api/auth'
import { useUserStore } from '@/store'
import { clearImageGenerationStorage } from '@/views/image-generation/composables/useImageGeneration'
import { useCreditStore } from '@/store/modules/credit'
import { useWorkspaceFolderStore } from '@/store/modules/folder'
import { isUserRole } from '@/constants/role'

const userStore = useUserStore()
const creditStore = useCreditStore()
const workspaceFolderStore = useWorkspaceFolderStore()

interface SessionResponse {
  auth: boolean
  model: 'ChatGPTAPI' | 'ChatGPTUnofficialProxyAPI'
}

export interface AuthState {
  token: string | undefined
  refreshToken: string | undefined
  token_dify: string | undefined
  token_pass_port: string | undefined
  appCode: string | undefined
  session: SessionResponse | null
  customJwt: string | undefined
}

export const useAuthStore = defineStore('auth-store', {
  state: (): AuthState => ({
    token: getToken(),
    session: null,
    token_dify: getTokenDify(),
    token_pass_port: getTokenPassPort(),
    refreshToken: getRefreshToken(),
    appCode: getAppCode(),
    customJwt: getCustomJWT(),
  }),

  getters: {
    isChatGPTAPI(state): boolean {
      return state.session?.model === 'ChatGPTAPI'
    },
  },

  actions: {
    setToken(token: string | undefined) {
      this.token = token
      setToken(token)
    },

    removeToken() {
      this.token = undefined
      removeToken()
    },

    setRefreshToken(token: string | undefined) {
      this.refreshToken = token
      setRefreshToken(token)
    },

    removeRefreshToken() {
      this.refreshToken = undefined
      removeRefreshToken()
    },

    setTokenDify(token: string | undefined) {
      if (token) {
        this.token_dify = token
        setTokenDify(token)
      }
    },

    removeTokenDify() {
      this.token_dify = undefined
      removeTokenDify()
    },

    setTokenPassPort(token: string | undefined) {
      if (token) {
        this.token_pass_port = token
        setTokenPassPort(token)
      }
    },

    setAppCode(appCode: string | undefined) {
      if (appCode) {
        this.appCode = appCode
        setAppCode(appCode)
      }
    },

    removeAppCode() {
      this.appCode = undefined
      removeAppCode()
    },

    setCustomJwt(jwt: string | undefined) {
      this.customJwt = jwt
      setCustomJWT(jwt)
    },

    removeCustomJwt() {
      this.token = undefined
      removeCustomJWT()
    },

    async login(
      req: {
        username: string
        password: string
      },
    ) {
      try {
        this.removeToken()
        const { data } = await loginApi<{
          data: Auth.LoginResponse
        }>(req)
        this.setToken(data.accessToken)
        this.setRefreshToken(data.refreshToken)
        this.setTokenDify(data.authDifyResponse?.accessTokenDify)
        this.setCustomJwt(data?.customJwt)
        userStore.updateUserInfo(data.userInfo)
        userStore.setIsFirstLogin(data.isFistLogin)

        // Set isCreatedWallet from response if available
        if (data.isCreatedWallet !== undefined)
          creditStore.setIsCreatedWallet(data.isCreatedWallet)
      }
      catch (error) {
        return Promise.reject(error)
      }
    },
    async loginGoogle(
      req: {
        accessToken: string
      },
    ) {
      try {
        this.removeToken()
        const { data } = await loginByGoogle<{
          data: Auth.LoginResponse
        }>(req)
        this.setToken(data.accessToken)
        this.setCustomJwt(data?.customJwt)
        this.setRefreshToken(data.refreshToken)
        this.setTokenDify(data.authDifyResponse?.accessTokenDify)
        userStore.updateUserInfo(data.userInfo)
        userStore.setIsFirstLogin(data.isFistLogin)

        // Set isCreatedWallet from response if available
        if (data.isCreatedWallet !== undefined)
          creditStore.setIsCreatedWallet(data.isCreatedWallet)
      }
      catch (error) {
        return Promise.reject(error)
      }
    },
    async logout() {
      try {
        if (this.refreshToken)
          await logoutApi<any>({ refreshToken: this.refreshToken })
      }
      catch (error) {
      }
      finally {
        const userRole = userStore.role
        this.removeToken()
        this.removeRefreshToken()
        this.removeTokenDify()
        // Clear image generation storage when logout
        clearImageGenerationStorage()
        // Reset isCreatedWallet when logout
        creditStore.resetIsCreatedWallet()
        userStore.removeWorkSpace()
        workspaceFolderStore.resetFolders()
        // router.push('/login')
        if (isUserRole(userRole, 'STUDENT'))
          router.push({ name: 'login-student' })
				 else
					 router.push({ name: 'login-teacher' })
      }
    },

  },
})

export function useAuthStoreWithout() {
  return useAuthStore(store)
}
